import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, CheckCircle2, Send } from 'lucide-react';

export const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    unternehmen: '',
    email: '',
    telefon: '',
    bessProject: '',
    leistungsbedarf: '',
    nachricht: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('https://formspree.io/f/xojpvboy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setIsSuccess(true);
      }
    } catch {
      // silent fail
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        unternehmen: '',
        email: '',
        telefon: '',
        bessProject: '',
        leistungsbedarf: '',
        nachricht: '',
      });
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      data-testid="contact-modal-overlay"
    >
      <Card
        ref={modalRef}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border shadow-2xl animate-in zoom-in-95 duration-200"
        data-testid="contact-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors z-10"
          data-testid="contact-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <CardContent className="p-8">
          {isSuccess ? (
            /* Success State */
            <div className="text-center py-8" data-testid="contact-modal-success">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-success" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-foreground mb-3">
                Vielen Dank!
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Wir melden uns zeitnah bei Ihnen.
              </p>
              <Button onClick={handleClose} variant="outline" data-testid="contact-modal-close-success">
                Schließen
              </Button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} data-testid="contact-form">
              {/* Header */}
              <div className="mb-6 pr-6">
                <h3 className="font-heading font-bold text-2xl text-foreground mb-2">
                  Kontaktieren Sie uns
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Sprechen Sie mit uns über BESS-Lösungen für Ihr Rechenzentrum.
                </p>
              </div>

              <div className="space-y-4">
                {/* Name & Unternehmen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      placeholder="Ihr Name"
                      data-testid="contact-field-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Unternehmen *</label>
                    <input
                      type="text"
                      name="unternehmen"
                      required
                      value={formData.unternehmen}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      placeholder="Firmenname"
                      data-testid="contact-field-company"
                    />
                  </div>
                </div>

                {/* Email & Telefon */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      placeholder="email@firma.de"
                      data-testid="contact-field-email"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Telefon <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input
                      type="tel"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      placeholder="+49 ..."
                      data-testid="contact-field-phone"
                    />
                  </div>
                </div>

                {/* Lead Qualification */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Planen Sie ein BESS-Projekt?</label>
                    <select
                      name="bessProject"
                      value={formData.bessProject}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      data-testid="contact-field-project"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="ja_planung">Ja, in Planung</option>
                      <option value="evaluieren">Wir evaluieren Optionen</option>
                      <option value="info">Nur Informationsanfrage</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Geschätzter Leistungsbedarf?</label>
                    <select
                      name="leistungsbedarf"
                      value={formData.leistungsbedarf}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      data-testid="contact-field-capacity"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="unter_1mw">&lt; 1 MW</option>
                      <option value="1_10mw">1–10 MW</option>
                      <option value="ueber_10mw">&gt; 10 MW</option>
                      <option value="unklar">Noch unklar</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Kurze Projektbeschreibung <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <textarea
                    name="nachricht"
                    rows={3}
                    value={formData.nachricht}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                    placeholder="Beschreiben Sie kurz Ihr Vorhaben..."
                    data-testid="contact-field-message"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 sm:flex-none"
                    data-testid="contact-modal-cancel"
                  >
                    Schließen
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 group"
                    data-testid="contact-modal-submit"
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
                    {!isSubmitting && <Send className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
